import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import type { ILogicGraph } from '@/interfaces/models';

/**
 * Repair only restored, inactive cross-coupled NOR latches. This is a load-time
 * operation, not a change to gate timing or a general oscillation suppressor.
 */
export function recoverSavedLatches(graph: ILogicGraph): boolean {
  // Iterative Kosaraju traversal avoids overflowing the stack on large circuits.
  const outgoing = new Map<NodeHandle, NodeHandle[]>();
  const incoming = new Map<NodeHandle, NodeHandle[]>();
  for (const id of graph.nodes.keys()) {
    outgoing.set(id, []);
    incoming.set(id, []);
  }
  for (const edge of graph.edges.values()) {
    // Older graph builds could leave edges pointing at removed wire nodes.
    // Only live endpoints participate in latch recovery.
    if (!graph.nodes.has(edge.sourceNodeId) || !graph.nodes.has(edge.targetNodeId))
      continue;
    outgoing.get(edge.sourceNodeId)!.push(edge.targetNodeId);
    incoming.get(edge.targetNodeId)!.push(edge.sourceNodeId);
  }
  const visited = new Set<NodeHandle>();
  const order: NodeHandle[] = [];
  for (const root of graph.nodes.keys()) {
    if (visited.has(root)) continue;
    visited.add(root);
    const stack: Array<[NodeHandle, number]> = [[root, 0]];
    while (stack.length) {
      const frame = stack[stack.length - 1];
      const targets = outgoing.get(frame[0])!;
      if (frame[1] < targets.length) {
        const next = targets[frame[1]++];
        if (!visited.has(next)) {
          visited.add(next);
          stack.push([next, 0]);
        }
      } else {
        order.push(frame[0]);
        stack.pop();
      }
    }
  }
  visited.clear();
  let recovered = false;
  for (const root of order.reverse()) {
    if (visited.has(root)) continue;
    const ids = new Set<NodeHandle>();
    const stack = [root];
    visited.add(root);
    while (stack.length) {
      const id = stack.pop()!;
      ids.add(id);
      for (const source of incoming.get(id)!) {
        if (!visited.has(source)) {
          visited.add(source);
          stack.push(source);
        }
      }
    }
    const nodes = [...ids].map((id) => graph.nodes.get(id)!);
    const gates = nodes.filter((node) => node.type === 'NOT');
    if (
      gates.length !== 2 ||
      nodes.some((node) => !['NOT', 'WIRE', 'DIODE'].includes(node.type))
    )
      continue;

    // Each inverter must see the other inverter, plus an external inactive
    // control. Excludes bare rings, self-inverters, delays and clock nodes.
    const controlledByOther = (gateId: NodeHandle, otherId: NodeHandle) => {
      const seen = new Set<NodeHandle>();
      const pending = [...incoming.get(gateId)!];
      let controlled = false;
      let feedback = false;
      while (pending.length) {
        const id = pending.pop()!;
        if (seen.has(id)) continue;
        seen.add(id);
        if (!ids.has(id)) {
          if (graph.nodes.get(id)!.output) return false;
          controlled = true;
        } else if (id === otherId) feedback = true;
        else if (id === gateId) return false;
        else for (const source of incoming.get(id)!) pending.push(source);
      }
      return controlled && feedback;
    };
    if (
      !controlledByOther(gates[0].id, gates[1].id) ||
      !controlledByOther(gates[1].id, gates[0].id)
    )
      continue;

    const values = new Map(nodes.map((node) => [node.id, node.output]));
    const expected = (id: NodeHandle) => {
      const input = incoming
        .get(id)!
        .some((source) => values.get(source) ?? graph.nodes.get(source)!.output);
      return graph.nodes.get(id)!.type === 'NOT' ? !input : input;
    };
    // A valid saved bit must never be replaced by a template's default bit.
    if (nodes.every((node) => node.output === expected(node.id))) continue;
    const passive = nodes.filter((node) => node.type !== 'NOT');
    const settlePassive = () => {
      for (let pass = 0; pass <= passive.length; pass++) {
        let changed = false;
        for (const node of passive) {
          const next = expected(node.id);
          if (next !== values.get(node.id)) {
            values.set(node.id, next);
            changed = true;
          }
        }
        if (!changed) break;
      }
    };
    // Work on a candidate, committing only a verified complementary fixed point.
    for (let pass = 0; pass < 4; pass++) {
      settlePassive();
      for (const gate of gates) {
        values.set(gate.id, expected(gate.id));
        settlePassive();
      }
      if (nodes.every((node) => values.get(node.id) === expected(node.id))) break;
    }
    if (
      values.get(gates[0].id) === values.get(gates[1].id) ||
      !nodes.every((node) => values.get(node.id) === expected(node.id))
    )
      continue;
    for (const node of nodes) node.output = values.get(node.id)!;
    recovered = true;
  }
  return recovered;
}
